import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import {
  actorLabel,
  formatLatency,
  isTruncatedBody,
  prettyJson,
  rowActorState,
  statusTone,
} from '@/lib/api-log-utils';
import type { ActorState, SimpleActorState } from '@/lib/api-log-utils';
import {
  actionLabel,
  describeActivity,
  moduleLabel,
} from '@/lib/activity-language';
import { cx } from '@/lib/cx';
import type { ApiLogDetail } from '@/lib/types';
import { Badge, Button, Modal, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { OUTCOME_TONES } from './outcome-tones';

/*
 * The audit log read as a business record.
 *
 * The order of this dialog is the whole point of it. A manager opens a row to
 * answer "who did what, to what, and did it work" — so the plain-English
 * sentence is the headline, the supporting facts come next, and every technical
 * value is folded away behind one disclosure. Support still needs all of it,
 * which is why nothing was deleted to get here; it was demoted.
 *
 * The humanising itself is NOT done here. `lib/activity-language.ts` owns the
 * single reading of a log row as a sentence, so the list, this dialog and the
 * stats page cannot drift into three different vocabularies for the same event.
 */

/* --- small shared bits --------------------------------------------------- */

/**
 * One labelled fact.
 *
 * `min-w-0` is not decoration: this sits inside a grid, and a grid item
 * defaults to `min-width: auto`, so a long unbroken value (a user agent, a
 * path with a fat query string) would push the whole dialog wider instead of
 * wrapping. The app is tested at 320px.
 */
function Item({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-fg-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function Group({
  title,
  columns = 'sm:grid-cols-2',
  children,
}: {
  title: string;
  columns?: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0 border-t border-border pt-3 first:border-t-0 first:pt-0">
      <h3 className="mb-3 text-xs font-semibold text-fg-secondary">
        {title}
      </h3>
      <dl className={`grid gap-x-4 gap-y-3 ${columns}`}>{children}</dl>
    </section>
  );
}

/**
 * A null that MEANS something.
 *
 * `route`, `module` and `recordId` are all nullable, and in every case the null
 * is a fact about the request rather than missing data — so it gets a sentence
 * instead of a bare dash.
 */
function Absent({ children }: { children: ReactNode }) {
  return <span className="text-fg-subtle italic">{children}</span>;
}

function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-xs [overflow-wrap:anywhere]">{children}</span>;
}

function ErrorBlock({ children }: { children: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" className="break-words">
      {children}
    </Notice>
  );
}

/*
 * The outcome as a tone: `OUTCOME_TONES` in `./outcome-tones`, ONE copy shared
 * with the feed. Purely presentational — the word always comes from
 * `ActivityDescription.outcomeLabel` and is always printed.
 */

/* --- request id ---------------------------------------------------------- */

/**
 * The request id is the one value support will ask for — it is the same string
 * the API returns as `x-request-id` — so it is copyable rather than merely
 * selectable.
 *
 * `navigator.clipboard` is absent on an insecure origin and can reject when the
 * document is not focused, so the failure path leaves the (already selectable)
 * text alone rather than pretending the copy worked.
 */
function RequestId({ requestId }: { requestId: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = useCallback(() => {
    navigator.clipboard?.writeText(requestId).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }, [requestId]);

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2">
      <Mono>{requestId}</Mono>
      <Button variant="ghost" size="sm" onClick={copy}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </span>
  );
}

/* --- bodies -------------------------------------------------------------- */

/**
 * `queryParams`, `requestBody` and `responseBody` are typed `unknown` on
 * purpose: MariaDB hands JSON columns back as strings where MySQL hands back
 * objects, and content that would not parse comes through raw. Nothing here
 * reaches into the value — `prettyJson` is the only thing that touches it, and
 * it never throws.
 *
 * Three outcomes, and the first is the common one:
 *
 *   null            not captured. On a GET that is NORMAL, not a failure —
 *                   bodies are stored for writes only.
 *   truncated       a body over 64KB is replaced WHOLESALE by
 *                   `{ _truncated, _bytes, _note }`. It is not a fragment of
 *                   the payload, so it is rendered as a marker with its byte
 *                   count rather than as a one-key JSON tree.
 *   present         pretty-printed, or shown verbatim when it was not JSON.
 *
 * These stay inside the technical disclosure. They are raw JSON, and no amount
 * of friendly labelling turns a payload into something a manager should read.
 */
function Body({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h4 className="text-xs font-semibold text-fg-secondary">
          {label}
        </h4>
        {isTruncatedBody(value) && (
          <Badge tone="warning">Truncated · {value._bytes.toLocaleString()} bytes</Badge>
        )}
      </div>
      <div className="mt-1.5">
        <BodyValue value={value} />
      </div>
    </div>
  );
}

function BodyValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return (
      <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-fg-muted">
        Not captured — bodies are stored for writes only.
      </p>
    );
  }

  if (isTruncatedBody(value)) {
    return (
      <Notice
        tone="warning"
        title={<>Replaced in full — {value._bytes.toLocaleString()} bytes, over the 64KB limit.</>}
      >
        <p className="m-0 [overflow-wrap:anywhere]">{value._note}</p>
        <p className="m-0 mt-1">Nothing of the payload was kept.</p>
      </Notice>
    );
  }

  const text = prettyJson(value);
  if (text === null) {
    return (
      <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-fg-muted">
        Stored, but not renderable as JSON.
      </p>
    );
  }

  /*
   * The `<pre>` scrolls inside its own box. A single long line — a base64 blob,
   * a minified payload — must never widen the dialog, and `min-w-0` on every
   * ancestor grid/flex child above is what lets `overflow-x: auto` here
   * actually bite.
   */
  return (
    <div className="max-h-72 min-w-0 overflow-x-auto overflow-y-auto rounded-md border border-border bg-surface-sunken">
      <pre className="w-max min-w-full px-3 py-2 font-mono text-xs text-fg-secondary">{text}</pre>
    </div>
  );
}

/* --- actor --------------------------------------------------------------- */

/**
 * Four genuinely different states, and conflating any two of them destroys the
 * point of the trail.
 *
 * A DELETED actor carries an email and no id — that email is a denormalised
 * copy kept precisely so the audit can still name the person. It renders as the
 * email plus an explicit "account deleted" marker, and never as "Anonymous".
 * "Anonymous" means something else entirely: a request that arrived with no
 * token at all.
 *
 * A SUPPORT SESSION is two people behind one request, and it is checked first —
 * the row's `actor` field alone cannot tell you which of the two it holds, so
 * any branch that reads it before the support-session check would render one
 * name and look finished.
 */
function Actor({ log }: { log: ApiLogDetail }) {
  const state = rowActorState(log);

  if (state.kind === 'support-session') return <SupportSessionActor state={state} />;

  if (state.kind === 'user') {
    return (
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-fg">{state.name}</span>
        {state.email && <EmailLink address={state.email} tone="muted" />}
        <Badge tone="neutral">User #{state.id}</Badge>
      </span>
    );
  }

  if (state.kind === 'deleted') {
    return (
      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <EmailLink address={state.email} className="font-medium" />
          <Badge tone="warning">Account deleted</Badge>
        </span>
        <span className="text-xs text-fg-muted">
          The account is gone; this email is the copy kept on the log row.
        </span>
      </span>
    );
  }

  if (state.kind === 'anonymous') {
    return (
      <span className="flex min-w-0 flex-col gap-1">
        <span className="font-medium text-fg">{actorLabel(state)}</span>
        <span className="text-xs text-fg-muted">
          Nobody was signed in.
        </span>
      </span>
    );
  }

  return <Absent>{actorLabel(state)}</Absent>;
}

/**
 * Both people behind one request, stacked and labelled by ROLE.
 *
 * The two rows are labelled "Acting" and "Acted as" rather than being run
 * together into one line, because this is the screen somebody opens to settle a
 * dispute about who changed a record — and at that moment the question is not
 * "who was involved" but "which of them did it". A single sentence answers the
 * first; only the labels answer the second.
 *
 * The explanatory line stays. It is not helper text under the house rule: it
 * names a state the reader cannot otherwise see — that full access, deletes
 * included, was exercised by one person through another's account — and a
 * reader who does not know what a support session is would otherwise take the
 * two names for a data error.
 */
function SupportSessionActor({
  state,
}: {
  state: Extract<ActorState, { kind: 'support-session' }>;
}) {
  return (
    <span className="flex min-w-0 flex-col gap-1.5">
      <span className="flex flex-wrap items-center gap-2">
        <Badge tone="warning">Support session</Badge>
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <Party label="Acting" state={state.operator} />
        <Party label="Acted as" state={state.target} />
      </span>
      <span className="text-xs text-fg-muted">
        One person acting through another&rsquo;s account. Both are named.
      </span>
    </span>
  );
}

/**
 * One party of a support session.
 *
 * `'unknown'` and `'anonymous'` both render as an explicit statement that the
 * row does not name this person, never as a blank or an omitted line. A missing
 * half here is the finding, so it has to occupy space and read as a gap — a
 * support session that quietly showed one name would be indistinguishable from
 * an ordinary action, which is the whole failure this screen guards against.
 */
function Party({ label, state }: { label: string; state: SimpleActorState }) {
  const named = state.kind === 'user' || state.kind === 'deleted';

  return (
    <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="w-16 shrink-0 text-xs font-semibold text-fg-muted">
        {label}
      </span>
      {state.kind === 'user' && (
        <>
          <span className="font-medium text-fg">{state.name}</span>
          {state.email && <EmailLink address={state.email} tone="muted" />}
          <Badge tone="neutral">User #{state.id}</Badge>
        </>
      )}
      {state.kind === 'deleted' && (
        <>
          <EmailLink address={state.email} className="font-medium" />
          <Badge tone="warning">Account deleted</Badge>
        </>
      )}
      {!named && <Absent>This row does not name them</Absent>}
    </span>
  );
}

/* --- states -------------------------------------------------------------- */

/** The skeleton mirrors the real layout so nothing jumps when the row lands. */
function LoadingBody() {
  return (
    <div className="flex flex-col gap-5">
      <div className="min-w-0">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="mt-2 h-5 w-full" />
        <Skeleton className="mt-1.5 h-5 w-2/3" />
      </div>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="min-w-0">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-1.5 h-4 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return 'This log row no longer exists.';
    }
    if (error.status === 403) return 'You do not have permission to read API logs.';
    return error.message;
  }
  return 'Could not load this log row.';
}

/* --- the modal ----------------------------------------------------------- */

/** Tagged with the id it was fetched for, so a stale result can be ignored. */
type LoadResult =
  | { id: number; kind: 'loaded'; log: ApiLogDetail }
  | { id: number; kind: 'error'; message: string };

export interface ApiLogDetailModalProps {
  /** Null = closed. Set = fetch and show that log row. */
  logId: number | null;
  onClose: () => void;
}

/**
 * The full record behind one activity row.
 *
 * Fetches its own detail rather than taking a row as a prop: the list endpoint
 * returns the summary shape, and the three body columns only exist on
 * `GET /api-logs/:id`. Refetches whenever `logId` changes so reopening on a
 * different row cannot show the previous one's payload.
 */
export function ApiLogDetailModal({ logId, onClose }: ApiLogDetailModalProps): ReactElement | null {
  /*
   * The outcome carries the id it belongs to, so "is this result still the one
   * we want?" is answered during render instead of by clearing state in the
   * effect. Reopening on a different row therefore shows a skeleton on the
   * first frame — never the previous row's payload under the new id.
   */
  const [result, setResult] = useState<LoadResult | null>(null);
  const [settledId, setSettledId] = useState<number | null>(null);

  useEffect(() => {
    if (logId === null) return;
    const controller = new AbortController();

    api
      .getApiLog(logId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setResult({ id: logId, kind: 'loaded', log: response.data });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setResult({ id: logId, kind: 'error', message: errorMessage(cause) });
      })
      .finally(() => {
        if (!controller.signal.aborted) setSettledId(logId);
      });

    return () => controller.abort();
  }, [logId]);

  if (logId === null) return null;

  const current = result?.id === logId ? result : null;
  const loading = current === null || settledId !== logId;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Activity detail"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading && <LoadingBody />}

      {!loading && current.kind === 'error' && <ErrorBlock>{current.message}</ErrorBlock>}

      {!loading && current.kind === 'loaded' && <Detail log={current.log} />}
    </Modal>
  );
}

function Detail({ log }: { log: ApiLogDetail }) {
  const activity = describeActivity(log);
  const area = moduleLabel(log.module);
  const happened = actionLabel(log.action);

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/*
       * The headline. One sentence, then the outcome as a WORD — the tone on
       * the badge is a second channel, never the signal itself.
       */}
      <header className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Badge tone={OUTCOME_TONES[activity.outcome]} dot>
            {activity.outcomeLabel}
          </Badge>
          {activity.routine && <Badge tone="neutral">Routine activity</Badge>}
          <span className="text-xs text-fg-muted">{formatDateTime(log.createdAt)}</span>
        </div>

        <p className="mt-2 text-base leading-snug font-medium text-fg [overflow-wrap:anywhere]">
          {activity.sentence}
        </p>

        {/*
         * A link only when the description actually resolved one. A record that
         * has no screen of its own — or a request that addressed no single
         * record — gets no link rather than a dead one.
         */}
        {activity.href && (
          <TextLink to={activity.href} className="mt-2 inline-block text-sm font-medium underline">
            Open {activity.object ?? 'the record'}
          </TextLink>
        )}
      </header>

      {/* Who, when, and what was affected — in business terms. */}
      <Group title="Summary">
        <Item label="Person">
          <Actor log={log} />
        </Item>

        <Item label="When">{formatDateTime(log.createdAt)}</Item>

        <Item label="Area">
          {area ? (
            area
          ) : (
            <Absent>Outside the business areas — sign-ins, health checks, permission look-ups.</Absent>
          )}
        </Item>

        <Item label="What happened">
          {happened ?? <Absent>No recognised action.</Absent>}
        </Item>

        <Item label="What was affected">
          {activity.object ? (
            <span className="text-fg">{activity.object}</span>
          ) : (
            <Absent>Nothing in the CRM — a sign-in, health check or permission look-up.</Absent>
          )}
        </Item>

        <Item label="Result">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-fg">{activity.outcomeLabel}</span>
            {log.errorCode && <Badge tone="danger">{log.errorCode}</Badge>}
          </span>
        </Item>
      </Group>

      <TechnicalDetails log={log} />
    </div>
  );
}

/**
 * Everything a support engineer needs and a manager does not, behind one
 * disclosure that is CLOSED on arrival.
 *
 * Nothing was removed to make the page readable — the method, the full URL, the
 * route pattern, the status code, the latency, the request id, the client and
 * all three payloads are still here, in one place, one click away.
 */
function TechnicalDetails({ log }: { log: ApiLogDetail }) {
  return (
    <details className="group min-w-0 rounded-lg border border-border bg-surface">
      <summary
        className={cx(
          'flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-lg px-3 py-2.5 select-none',
          'text-sm font-medium text-fg-secondary',
          'transition-colors duration-150 ease-out hover:bg-surface-hover hover:text-fg motion-reduce:transition-none',
          '[&::-webkit-details-marker]:hidden',
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-fg-subtle transition-transform duration-150 ease-out group-open:rotate-90 motion-reduce:transition-none"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
        <span className="min-w-0 flex-1">Technical details</span>
        <span className="text-2xs text-fg-subtle max-sm:hidden">For support</span>
      </summary>

      <div className="flex min-w-0 flex-col gap-5 border-t border-border px-3 py-4">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Badge tone="neutral">{log.method}</Badge>
          {/*
           * The status code is never colour-only: the tone sits on top of the
           * number, which is always spelled out.
           */}
          <Badge tone={statusTone(log.statusCode)} dot>
            HTTP {log.statusCode}
          </Badge>
          <Badge tone="neutral">{formatLatency(log.durationMs)}</Badge>
          {log.errorCode && <Badge tone="danger">{log.errorCode}</Badge>}
        </div>

        <Group title="Request">
          <div className="min-w-0 sm:col-span-2">
            <Item label="Request ID">
              <RequestId requestId={log.requestId} />
            </Item>
          </div>

          <div className="min-w-0 sm:col-span-2">
            <Item label="URL (path + query string)">
              <Mono>{log.path}</Mono>
            </Item>
          </div>

          <Item label="Route pattern">
            {log.route ? (
              <Mono>{log.route}</Mono>
            ) : (
              <Absent>No route matched (a 404).</Absent>
            )}
          </Item>

          <Item label="Status code">
            <Mono>{log.statusCode}</Mono>
          </Item>

          <Item label="Duration">{formatLatency(log.durationMs)}</Item>

          <Item label="Permission module">
            {log.module ? (
              <Mono>{log.module}</Mono>
            ) : (
              <Absent>Outside any module — /auth, /health and the permission endpoints.</Absent>
            )}
          </Item>

          <Item label="Action slug">
            {log.action ? <Mono>{log.action}</Mono> : <Absent>No action recorded.</Absent>}
          </Item>

          <Item label="Record ID">
            {log.recordId ? (
              <Mono>{log.recordId}</Mono>
            ) : (
              <Absent>A collection route — no single record.</Absent>
            )}
          </Item>
        </Group>

        <Group title="Client">
          <Item label="IP address">
            {log.ip ? <Mono>{log.ip}</Mono> : <Absent>Not recorded.</Absent>}
          </Item>
          <Item label="User agent">
            {log.userAgent ? <Mono>{log.userAgent}</Mono> : <Absent>Not recorded.</Absent>}
          </Item>
        </Group>

        <section className="flex min-w-0 flex-col gap-4 border-t border-border pt-3">
          <div>
            <h3 className="text-xs font-semibold text-fg-secondary">
              Payloads
            </h3>
          </div>

          <Body label="Query parameters" value={log.queryParams} />

          <Body label="Request body" value={log.requestBody} />

          <Body label="Response body" value={log.responseBody} />
        </section>
      </div>
    </details>
  );
}
